import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const YEAR_OPTIONS = [2024, 2025, 2026];

interface MonthRangeSelectorProps {
  /** Current range start, "YYYY-MM-DD". */
  dateFrom: string;
  /** Current range end, "YYYY-MM-DD". */
  dateTo: string;
  /** Emits a new whole-month range when a month is picked. */
  onChange: (next: { dateFrom: string; dateTo: string }) => void;
}

/**
 * Fast month shortcut that drives the page's Desde/Hasta range. The range stays
 * the single source of truth: this control is derived from dateFrom/dateTo and
 * only emits new bounds — it holds no state of its own. Picking a month (via the
 * dropdowns or the prev/next arrows) sets the range to that calendar month; when
 * the range isn't a whole month the Mes dropdown shows "Personalizado".
 */
export function MonthRangeSelector({
  dateFrom,
  dateTo,
  onChange,
}: MonthRangeSelectorProps) {
  const anchor = dayjs(dateFrom);

  // True only when the current range is exactly one whole calendar month.
  const isWholeMonth =
    dayjs(dateFrom).isSame(anchor.startOf("month"), "day") &&
    dayjs(dateTo).isSame(anchor.endOf("month"), "day");

  const displayYear = anchor.year();
  const displayMonthIdx = anchor.month(); // 0-indexed
  const monthValue = isWholeMonth ? String(displayMonthIdx + 1) : "";
  const yearValue = String(displayYear);

  const selectMonth = (d: dayjs.Dayjs) =>
    onChange({
      dateFrom: d.startOf("month").format("YYYY-MM-DD"),
      dateTo: d.endOf("month").format("YYYY-MM-DD"),
    });

  // Build from day 01 so the dayjs month setter can't overflow (e.g. Jan 31 → Mar).
  const monthStart = (year: number, month1to12: number) =>
    dayjs(`${year}-${String(month1to12).padStart(2, "0")}-01`);

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium">Mes</Label>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-xl"
          aria-label="Mes anterior"
          onClick={() => selectMonth(anchor.subtract(1, "month"))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Select
          value={monthValue}
          onValueChange={(v) => selectMonth(monthStart(displayYear, Number(v)))}
        >
          <SelectTrigger className="h-9 w-[130px] rounded-xl">
            <SelectValue placeholder="Personalizado" />
          </SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((name, i) => (
              <SelectItem key={name} value={String(i + 1)}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={yearValue}
          onValueChange={(v) =>
            selectMonth(monthStart(Number(v), displayMonthIdx + 1))
          }
        >
          <SelectTrigger className="h-9 w-[100px] rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEAR_OPTIONS.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-xl"
          aria-label="Mes siguiente"
          onClick={() => selectMonth(anchor.add(1, "month"))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
